import { IconButton } from '@material-ui/core';
import Slider from '@material-ui/core/Slider';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import cx from 'classnames';
import DownloadIcon from 'components/base/icons/DownloadIcon';
import EyeIcon from 'components/base/icons/EyeIcon';
import { btn_opacity, icon_mesh_off, icon_mesh_on } from 'components/base/images';
import CircularLoading from 'components/base/loading/CircularLoading';
import CustomCheckbox from 'components/common/checkbox/CustomCheckbox';
import T from 'components/common/text/T';
import { CloudFile } from 'contexts/project/ProjectContext';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { color } from 'styles/utils';
import ProjectFileIcon from './ProjectFileIcon';
import { IConvertFile } from 'lib/types/clinic/fileTypes';
import { fetchProjectFileDownloadUrl } from 'api/file';
import { downloadFile, downloadFileWithElectron, newDownloadFileWithElectron } from 'store/utils';
import { AppActions, ProjectActions } from 'store/actionCreators';
import { projectEventType } from 'lib/mapper';
import { v4 as uuid } from 'uuid';

const imgTypes = ['png', 'jpg', 'jpeg', 'gif'];
const modelTypes = ['obj', 'ply', 'stl'];

interface ProjectFileGroupProps {
  company: string;
  projectCode: string;
  projectId: string;
  hasViewer: boolean;
  fileList: any;
  title: string;
  shareType: string;
  isModelFileLoadingProgress: {
    value: boolean;
    onChange: (e: boolean) => void;
    setValue: React.Dispatch<any>;
  };
  selectedModelFile: any;
  onClickCheckbox: (e: any, id: any, storage: string, data: any) => void;
  onClickCheckboxAll: (e: any, storage: string, isCheckAll: boolean, shareType: string) => void;
  onClickWireframe: (id: number, data: any) => void;
  onChangeModelOpacity: (e: any, id: number, newValue: number | number[]) => void;
  onClickImageFile: (e: any, id: any, data?: any) => Promise<any>;
  onClickViewModelFile: (e: any, id: any, data?: any) => Promise<any>;
  onClickDeleteFiles: (storage: string, deletelist: any[]) => void;
  onClickDownloadFiles: (id: number, name: string) => Promise<any>;
}
function ProjectFileGroup({
  company,
  projectCode,
  projectId,
  hasViewer,
  fileList,
  title,
  shareType,
  onClickWireframe,
  onClickImageFile,
  onClickViewModelFile,
  onChangeModelOpacity,
  isModelFileLoadingProgress,
  selectedModelFile,
  onClickCheckbox,
  onClickCheckboxAll,
  onClickDeleteFiles,
  onClickDownloadFiles,
}: ProjectFileGroupProps) {
  const { ipcRenderer } = window;

  const [isCheckAll, setIsCheckAll] = useState(false);
  const [checkedList, setCheckedList] = useState<number[]>([]);

  useEffect(() => {
    if (fileList.value.length === 0) {
      setIsCheckAll(false);
      return;
    }
    let checkedCount = 0;
    let myFilesCount = 0;

    fileList.value.forEach((item: any) => {
      if (!!item.isChecked) {
        checkedCount++;
      }
      if (!!item.isOwner) {
        myFilesCount++;
      }
    });

    if (myFilesCount === checkedCount && myFilesCount !== 0) {
      setIsCheckAll(true);
    } else {
      setIsCheckAll(false);
    }
  }, [fileList.value]);

  const checkboxOnClickAll = (e: any) => {
    setIsCheckAll(!isCheckAll);
    onClickCheckboxAll(_, 'cloud', isCheckAll, shareType);
  };

  const handleClickDelete = useCallback(() => {
    // const deleteIdList = checkedList.map((item: any) => {
    //   return item.cloudDataIdx;
    // });
    onClickDeleteFiles(shareType, checkedList);
  }, [checkedList, shareType]);

  const handleClickFileDownloadAll = async (fileList: IConvertFile[]) => {
    if (!ipcRenderer) return;

    for (const file of fileList) {
      if (!file.cloudDataIdx) continue;

      try {
        const { data } = await fetchProjectFileDownloadUrl({ cloudDataIdx: file.cloudDataIdx });
        const { signedUrl } = data;
        if (ipcRenderer) {
          // NOTE: Electron download
          const uid = uuid();

          ipcRenderer.send('projectFileDownload', {
            url: signedUrl,
            properties: {
              projectCode,
              projectId: projectId.normalize('NFC'),
              fileName: file.originName.normalize('NFC'),
              uid,
            },
          });

          // 동기적으로 실행될 때까지 기다림
          await newDownloadFileWithElectron({
            name: file.originName.normalize('NFC'),
            url: signedUrl,
            properties: {
              projectCode,
              projectId: projectId.normalize('NFC'),
              fileName: file.originName.normalize('NFC'),
            },
            type: 'download',
            uid,
          });
        } else {
          downloadFile({
            url: signedUrl,
            name: file.originName.normalize('NFC'),
          });
        }
      } catch (error) {
        AppActions.show_toast({
          type: 'error',
          message: (error as { message: string })?.message,
        });
      }
      handleCreateProjectHistory(fileList, company);
    }
  };

  // const handleClickFileDownloadAll = (fileList: IConvertFile[]) => {
  //   // if (!ipcRenderer) return;
  //   fileList.forEach(async (file: IConvertFile) => {
  //     if (!file.cloudDataIdx) return;
  //     try {
  //       const { data } = await fetchProjectFileDownloadUrl({ cloudDataIdx: file.cloudDataIdx });
  //       const { signedUrl } = data;
  //       if (ipcRenderer) {
  //         // NOTE: Electron download
  //         downloadFileWithElectron({
  //           name: file.originName.normalize('NFC'),
  //           url: signedUrl,
  //           properties: {
  //             projectCode,
  //             projectId: projectId.normalize('NFC'),
  //             fileName: file.originName.normalize('NFC'),
  //           },
  //           type: 'download',
  //         });
  //       } else {
  //         downloadFile({
  //           url: signedUrl,
  //           name: file.originName.normalize('NFC'),
  //         });
  //       }
  //     } catch (error) {
  //       AppActions.show_toast({
  //         type: 'error',
  //         message: (error as { message: string })?.message,
  //       });
  //     }
  //     handleCreateProjectHistory(fileList, company);
  //   });
  // };

  const handleCreateProjectHistory = (fileList: IConvertFile[], company: string) => {
    let name = '';
    fileList.forEach((item: IConvertFile, idx: number) => {
      if (idx === 0) {
        name += `${item.originName}`;
      } else {
        name += `, ${item.originName}`;
      }
    });
    const createHistoryData = {
      projectCode,
      type: projectEventType.fileDownload,
      params: {
        content: `${company}, ${name}`,
      },
    };
    console.log('HDS] handleCreateProjectHistory ____', createHistoryData);

    ProjectActions.create_project_history_request(createHistoryData);
  };

  useEffect(() => {
    const checkedList = fileList.value.filter((item: any) => {
      return !!item.isOwner && !!item.isChecked;
    });
    setCheckedList([...checkedList]);
  }, [fileList.value]);

  return (
    <StyledProjectFileGroup data-component-name="ProjectFileGroup">
      <div className="projectFileGroup__header">
        <div className="projectFileGroup__title_box">
          {useMemo(
            () => (
              <label
                className={cx('checkBox', {
                  isHover: fileList.value?.length > 0,
                })}
              >
                <CustomCheckbox
                  marginRight={0}
                  width={15}
                  disabled={fileList.value.length === 0}
                  checked={!!isCheckAll}
                  onClick={e => {
                    checkboxOnClickAll(e);
                  }}
                />
              </label>
            ),
            [isCheckAll, fileList.value],
          )}
          <div className="projectFileGroup__title">{title}</div>
        </div>
        <div className="projectFileGroup__button_box">
          <IconButton
            className={cx('projectFileGroup__button', {
              on: fileList.value.length > 0,
            })}
            onClick={() => {
              handleClickFileDownloadAll(fileList.value);
            }}
            disabled={fileList.value.length === 0}
          >
            <T>GLOBAL_DOWNLOAD_ALL</T>
          </IconButton>
          <IconButton
            className={cx('projectFileGroup__button', {
              on: checkedList.length > 0,
            })}
            onClick={handleClickDelete}
            disabled={checkedList.length === 0}
          >
            <T>GLOBAL_DELETE</T>
          </IconButton>
        </div>
      </div>
      <div className="projectFileGroup__body">
        {useMemo(
          () => (
            <div className="projectFileGroup__file_list">
              {fileList.value?.length > 0 &&
                fileList.value.map((item: CloudFile, idx: number) => {
                  const itemType = item.type || '';
                  const isModelType = modelTypes.includes(itemType.toLocaleLowerCase());
                  const isImageType = [...imgTypes].includes(itemType.toLocaleLowerCase());
                  const isViewType =
                    isModelType || isImageType || itemType.toLocaleLowerCase() === 'html';
                  const activeEyeColor = item.viewColor || color.blue;
                  const unactiveEyeColor = isViewType ? color.gray_b5 : color.gray_deep_weak;
                  const eyeIconColor = item.isView ? activeEyeColor : unactiveEyeColor;
                  return (
                    <div
                      className={cx('projectFileGroup__file_row', {
                        isDisplayed: item.isDisplayed,
                      })}
                      key={item.cloudDataIdx}
                    >
                      <div className="projectFileGroup__file_row_cell check_item">
                        <label className="">
                          <CustomCheckbox
                            marginRight={0}
                            width={15}
                            disabled={!item.isOwner}
                            checked={item.isChecked}
                            onChange={e => {
                              onClickCheckbox(_, item.cloudDataIdx, 'cloud', {
                                shareType,
                              });
                            }}
                          />
                        </label>
                      </div>
                      <div className="projectFileGroup__file_row_cell extestion">
                        <ProjectFileIcon fileName={item.originName} projectId={projectId} />
                      </div>
                      <div className="projectFileGroup__file_row_cell filename">
                        <span
                          className="projectUpload__file_list_item_text text-overflow-ellipsis"
                          title={item.originName}
                        >
                          {item.originName}
                        </span>
                        {!!isModelType && (
                          <div className="slider_box">
                            <FileSlider
                              disabled={!item.isView || !!isModelFileLoadingProgress.value}
                              defaultValue={100}
                              aria-label="Small"
                              valueLabelDisplay="auto"
                              onChange={(e, newVal) => {
                                onChangeModelOpacity(e, item.cloudDataIdx, newVal);
                              }}
                              value={item.opacity}
                              ValueLabelComponent={ValueLabelComponent}
                            />
                          </div>
                        )}
                      </div>
                      <div className="projectFileGroup__file_row_cell actions">
                        <div className="actions_icon_wrapper">
                          {hasViewer && !!isViewType && (
                            <IconButton
                              className="actions_icon"
                              disabled={!isViewType || !!isModelFileLoadingProgress.value}
                              onClick={() => {
                                if ('html' === itemType.toLocaleLowerCase()) {
                                  return onClickImageFile(_, item.cloudDataIdx, {
                                    action: 'html',
                                    shareType,
                                    value: {
                                      isView: item.isView,
                                    },
                                  });
                                }
                                if (imgTypes.includes(itemType.toLocaleLowerCase())) {
                                  return onClickImageFile(_, item.cloudDataIdx, {
                                    action: 'image',
                                    shareType,
                                    value: {
                                      isView: item.isView,
                                    },
                                  });
                                }
                                if (modelTypes.includes(itemType.toLocaleLowerCase())) {
                                  console.log('PMS] 여기 아닌가ㅏㅏㅏㅏㅏㅏㅏ');
                                  return onClickViewModelFile(_, item.cloudDataIdx, {
                                    action: 'toggleViewCloudModelFile',
                                    shareType,
                                    value: {
                                      name: item.originName,
                                      type: item.type,
                                      isView: item.isView,
                                      viewColor: item.viewColor,
                                      resourceFileList: item.resourceFileList,
                                      groupName: item.groupName,
                                    },
                                  });
                                }
                              }}
                            >
                              {!!isModelFileLoadingProgress.value ? (
                                item.cloudDataIdx === selectedModelFile ? (
                                  <CircularLoading size={15} />
                                ) : (
                                  <EyeIcon width={18} color={color.gray_deep_weak} />
                                )
                              ) : (
                                <EyeIcon width={18} color={eyeIconColor} />
                              )}
                            </IconButton>
                          )}
                        </div>

                        <div className="actions_icon_wrapper">
                          {!!isModelType && (
                            <IconButton
                              className="actions_icon"
                              disabled={!!isModelFileLoadingProgress.value}
                              onClick={() => {
                                onClickWireframe(item.cloudDataIdx, {
                                  wireFrame: item.isWireframe,
                                });
                              }}
                            >
                              {!item.isView || !item.isWireframe ? (
                                <img src={icon_mesh_off} />
                              ) : (
                                <img src={icon_mesh_on} />
                              )}
                            </IconButton>
                          )}
                        </div>
                        {/* 0: 업로드중, 1: 서버에 업로드, 2: s3업로드, 3: 에러 */}
                        <div className="actions_icon_wrapper">
                          <>
                            {item.uploadState === 0 && (
                              <IconButton className="actions_icon" disabled>
                                <CircularLoading size={15} />
                              </IconButton>
                            )}
                            {item.uploadState === 2 && (
                              <IconButton
                                className="actions_icon"
                                // disabled={!isViewType}
                                onClick={() =>
                                  onClickDownloadFiles(item.cloudDataIdx, item.originName)
                                }
                              >
                                <>
                                  {item.isDownload ? (
                                    <DownloadIcon width={16} color={color.gray_b5} />
                                  ) : (
                                    <DownloadIcon width={16} color={color.gray_b5} />
                                  )}
                                </>
                              </IconButton>
                            )}
                            {item.uploadState === 3 && (
                              <IconButton disabled>
                                <DownloadIcon width={16} color={color.gray_deep_weak} />
                              </IconButton>
                            )}
                          </>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          ),
          [fileList.value, isModelFileLoadingProgress.value],
        )}
      </div>
    </StyledProjectFileGroup>
  );
}

const StyledProjectFileGroup = styled.div<{}>`
  width: 100%;
  .projectFileGroup__header {
    height: 38px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-top: 1px solid #e1e1e7;
    border-bottom: 1px solid #e1e1e7;
    .checkBox {
      &:hover {
        cursor: pointer;
      }
    }
    .projectFileGroup__title_box {
      padding-left: 15px;
      display: flex;
      column-gap: 15px;
      .projectFileGroup__title {
        font-size: 14px;
        font-weight: 600;
        color: #25256f;
        display: flex;
        align-items: center;
      }
    }
    .projectFileGroup__button_box {
      padding-right: 10px;
      display: flex;
      column-gap: 10px;
      .projectFileGroup__button {
        display: flex;
        align-items: center;
        padding: 0;
        border-radius: 2px;
        font-size: 13px;
        font-weight: 400;
        color: #ced2df;
        &.on {
          color: #25256f;
        }
      }
    }
  }

  .projectFileGroup__body {
    width: 400px;
    height: 172px;
    /* border-bottom: 1px solid #e1e1e7; */
    .projectFileGroup__file_list {
      width: 400px;
      /* height: 162px; */
      height: 172px;
      padding: 5px 0 0 10px;
      position: relative;
      overflow-y: auto;
      /* background-color: ; */
    }
  }

  .projectFileGroup__file_row {
    background-color: #f9f9fb;
    border-radius: 10px;
    /* width: 384px; */
    width: 100%;
    max-width: 380px;
    /* height: 42px; */
    display: flex;
    align-items: center;
    justify-content: flex-start;
    column-gap: 10px;
    margin-bottom: 5px;
    padding: 5px 10px;

    .projectFileGroup__file_row_cell {
      -webkit-touch-callout: none;
      -webkit-user-select: none;
      -khtml-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
      user-select: none;

      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      label {
        &.isHover {
          cursor: pointer;
        }
      }
      &.check_item {
        /* margin-left: 15px; */
      }
      &.filename {
        flex-direction: column;
        /* width: 228px; */
        /* width: 148px; */
        width: 100%;
        .projectUpload__file_list_item_text {
          display: inline-block;
          /* width: 228px; */
          /* width: 148px; */
          max-width: 160px;
          width: 100%;
          font-size: 13px;
          color: #666666;
          line-height: 18px;
        }
        .slider_box {
          /* width: 228px; */
          /* width: 148px; */
          max-width: 160px;
          width: 100%;
          /* height: 24px; */
          height: 15px;
          .MuiSlider-root {
            color: #25256f;
            height: 4px;
            padding: 0;
            .MuiSlider-rail {
              height: 4px;
              border-radius: 2px;
              background-image: linear-gradient(to bottom, #949494, #b1b1b1 52%, #d9d9d9);
            }
            .MuiSlider-track {
              height: 4px;
              border-radius: 2px;
            }
            .MuiSlider-valueLabel {
              left: calc(-50% - 2px);
            }
            .MuiSlider-thumb {
              height: 14px;
              width: 14px;
              background-color: transparent;
              background-image: url(${btn_opacity});
              background-size: cover;
              background-repeat: no-repeat;

              &:focus,
              &:hover,
              &:active {
                box-shadow: inherit;
              }
            }

            &.Mui-disabled {
              color: #25256f;
              height: 4px;
              padding: 0;
              opacity: 0.6;

              .MuiSlider-thumb {
                margin-top: -5px;
                margin-left: -6px;
              }
            }
          }
        }
      }

      &.actions {
        column-gap: 13px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        .actions_icon_wrapper {
          width: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          .actions_icon {
            width: 24px;
            height: 24px;
            padding: 0;
          }
        }
      }
    }
  }
`;

export default React.memo(ProjectFileGroup);

const FileSlider = withStyles({})(Slider);

interface ValueLabelComponentProps {
  children: any;
  open: boolean;
  value: number;
}
function ValueLabelComponent(props: ValueLabelComponentProps) {
  const { children, open, value } = props;
  const classes = useStyles();

  return (
    <Tooltip
      open={open}
      enterTouchDelay={0}
      placement="top"
      title={value}
      classes={{ tooltip: classes.tooltip }}
    >
      {children}
    </Tooltip>
  );
}

const useStyles = makeStyles({
  tooltip: {
    marginBottom: '4px',
  },
});
